/*
 * flagship.js
 * Main Flagship frontend class to manage core functionality
 */

import axios from "axios";
import store from "../store/store";
import globalConfig from "../config/global";
import configStandard from "../config/instances/standard";
import configSHIFT from "../config/instances/SHIFT";
import { browserHistory } from "react-router";
import * as types from "../config/actionTypes";
import LogRocket from "logrocket";
import "regenerator-runtime/runtime";

class Flagship {
  constructor() {
    this.configure = this.configure.bind(this);
    this.getInstance = this.getInstance.bind(this);
    this.setInstance = this.setInstance.bind(this);
    LogRocket.init("5bg7zu/flagship");
    this.configure();
  }

  getInstance(userInstance) {
    // Default
    let instance = "Standard";

    // If previous instance viewed is saved in local storage
    let storedInstance = localStorage.getItem("flagship-instance");
    if (storedInstance !== null) {
      instance = storedInstance;
    }

    // If user can only access one instance
    if (userInstance !== null && userInstance !== "") {
      instance = userInstance;
    }

    this.instance = instance;
    this.configure();
    return instance;
  }

  setInstance(which) {
    this.instance = which;
    localStorage.setItem("flagship-instance", which);
    this.configure();
  }

  isPositiveInteger(x) {
    // http://stackoverflow.com/a/1019526/11236
    return /^\d+$/.test(x);
  }

  compareVersionNumbers(v1, v2) {
    let self = this;
    let v1parts = v1.split(".");
    let v2parts = v2.split(".");

    // First, validate both numbers are true version numbers
    function validateParts(parts) {
      for (let i = 0; i < parts.length; ++i) {
        if (!self.isPositiveInteger(parts[i])) {
          return false;
        }
      }
      return true;
    }
    if (!validateParts(v1parts) || !validateParts(v2parts)) {
      return NaN;
    }

    for (let i = 0; i < v1parts.length; ++i) {
      if (v2parts.length === i) {
        return 1;
      }

      if (v1parts[i] === v2parts[i]) {
        continue;
      }
      if (v1parts[i] > v2parts[i]) {
        return 1;
      }
      return -1;
    }

    if (v1parts.length !== v2parts.length) {
      return -1;
    }

    return 0;
  }

  configure() {
    if (this.instance === null) {
      this.instance = "Standard";
    }

    let envConfig = {
      env: "dev",
      apiBase: "http://flagship.api/",
      uploads: "https://flagship-user-photos.s3.amazonaws.com/",
      awsBucket: "dev-flagship-crm",
      url: "https://" + window.location.hostname,
    };

    if (
      window.location.hostname === "new.flagship.2060digital.com" ||
      window.location.hostname === "shift.flagship.2060digital.com" ||
      window.location.hostname === "flagship.2060digital.com"
    ) {
      envConfig = {
        env: "production",
        apiBase: "https://api.flagship.2060digital.com/",
        uploads: "https://flagship-user-photos.s3.amazonaws.com/",
        awsBucket: "dev-flagship-crm",
        url: "https://" + window.location.hostname,
      };
    } else if (window.location.hostname === "test.flagship.2060digital.com") {
      envConfig = {
        env: "staging",
        apiBase: "https://api.test.flagship.2060digital.com/",
        uploads: "https://flagship-user-photos.s3.amazonaws.com/",
        awsBucket: "dev-flagship-crm",
        url: "https://" + window.location.hostname,
      };
    }

    let instanceConfig = configStandard;

    if (this.instance === "SHIFT") {
      instanceConfig = configSHIFT;
    }

    this.config = Object.assign(instanceConfig, globalConfig, envConfig);

    return this.config;
  }

  get(endpoint, query, callback, error) {
    let self = this;
    let token = localStorage.getItem("flagshipToken");

    store.dispatch({
      type: types.BEGIN_REQUEST,
    });

    const prepareQuery = (data) => {
      Object.keys(data).map((param) => {
        if (typeof data[param] === "undefined" || data[param] === null) {
          delete data[param];
        }
      });
      let newData = new URLSearchParams(data).toString();
      return newData;
    };

    query.instance = this.instance;

    let url =
      this.config.apiBase +
      endpoint +
      (query !== {} ? "?" + prepareQuery(query) : "");

    let config = {
      headers: { "X-Flagship-Token": token },
    };

    let result = new Promise((resolve) => {
      let r = resolve;
      axios
        .get(url, config)
        .then(function (response) {
          if (
            self.compareVersionNumbers(
              response.headers["x-flagship-version"],
              self.config.version,
            ) > 0
          ) {
            store.dispatch({
              type: types.VERSION_MISMATCH,
              data: true,
            });
          } else {
            store.dispatch({
              type: types.VERSION_MISMATCH,
              data: false,
            });
          }

          store.dispatch({
            type: types.END_REQUEST,
          });

          if (typeof callback === "function") {
            callback(response);
          }
          r(true);
        })
        .catch(function (err) {
          store.dispatch({
            type: types.END_REQUEST,
          });

          if (typeof error === "function") {
            error(err);
          }
          return false;
        });
      return result;
    });
  }

  post(endpoint, data, callback, error, ignoreToken = false) {
    let token = localStorage.getItem("flagshipToken");

    if (token === null && ignoreToken !== true) {
      return false;
    }

    let self = this;

    store.dispatch({
      type: types.BEGIN_REQUEST,
    });

    data.instance = this.instance;

    let url = this.config.apiBase + endpoint;
    let config = {
      headers: {
        "X-Flagship-Token": token,
        "Content-Type": "application/x-www-form-urlencoded",
      },
      "X-Flagship-Token": token,
      ...data,
    };

    let result = new Promise((resolve) => {
      let r = resolve;

      axios
        .post(url, config)
        .then(function (response) {
          store.dispatch({
            type: types.END_REQUEST,
          });

          if (typeof callback === "function") {
            callback(response);
          }
          r(true);
        })
        .catch(function (err) {
          store.dispatch({
            type: types.END_REQUEST,
          });

          if (typeof error === "function") {
            error(err);
          } else {
            self.notify("An API error occurred.", "error");
          }
          return false;
        });
    });

    return result;
  }

  log(action, data = "", target = null, user = null) {
    let logData = {
      action,
      data,
      target,
      user,
    };

    this.post("log/write", logData);
  }

  notify(message, type = "info") {
    store.dispatch({
      type: types.SET_NOTIFICATION,
      payload: {
        type,
        message,
      },
    });
  }

  event(action, data = "", target = null, user = null) {
    this.log(action, data, target, user);
    this.sendMail(action, data, target, user);
  }

  error(message, target, user) {
    this.log("error", message, target, user);
    this.notify(message, "error");
  }

  closeSidebarNav() {
    store.dispatch({
      type: types.CLOSE_SIDEBAR_NAV,
    });
  }

  toggleSidebarNav() {
    store.dispatch({
      type: types.TOGGLE_SIDEBAR_NAV,
    });
  }

  redirect(path) {
    browserHistory.push(path);
  }

  changeRecordStatus(record, status, callback) {
    this.post(
      "change-record-status/" + record + "/" + status,
      {},
      function (response) {
        if (typeof callback === "function") {
          callback(response);
        }
      },
    );
  }

  sendMail(action, data, target, user, callback) {
    let self = this;

    let style =
      "display:inline-block; padding:6px 12px; color:#ffffff; border-radius:5px; ";

    // Prepare certain data for server-side (where action involves status change & global is not available)
    if (action === "change-status-io") {
      switch (this.config.records.io.statuses[data].color) {
        case "green":
          style += "background-color:#21BA45;";
          break;

        case "blue":
          style += "background-color:#2185D0;";
          break;

        case "purple":
          style += "background-color:#a333c8;";
          break;
        case "orange":
          style += "background-color:#f2711c;";
          break;
        case "yellow":
          style += "background-color:#FBBD08;";
          break;
        case "red":
          style += "background-color:#DB2828;";
          break;
        default:
          style += "background-color:#333";
          break;
      }
      data =
        '<span class="status-label" style="' +
        style +
        '">' +
        this.config.records.io.statuses[data].label +
        "</span>";
    } else if (action === "change-status-rfp") {
      switch (this.config.records.rfp.statuses[data].color) {
        case "green":
          style += "background-color:#21BA45;";
          break;

        case "blue":
          style += "background-color:#2185D0;";
          break;

        case "purple":
          style += "background-color:#a333c8;";
          break;
        case "orange":
          style += "background-color:#f2711c;";
          break;
        case "yellow":
          style += "background-color:#FBBD08;";
          break;
        case "red":
          style += "background-color:#DB2828;";
          break;
        default:
          style += "background-color:#333";
          break;
      }
      data =
        '<span class="status-label" style="' +
        style +
        '">' +
        this.config.records.rfp.statuses[data].label +
        "</span>";
    } else if (action === "proposal-upload") {
      style =
        "display:inline-block;padding:6px 12px; color:#ffffff; border-radius:5px; background-color:#a333c8;";

      let filename = data;
      data = {
        filename: filename,
        label:
          '<span class="status-label" style="' + style + '">Proposal</span>',
      };
    }
    this.post(
      "mail/" + action,
      {
        record_id: target,
        user_id: user,
        data: data,
        config: self.config,
      },
      function (response) {
        if (typeof callback === "function") {
          callback(response);
        }
      },
      function (err) {
        self.log(
          "error",
          "Error sending mail (action: " + action + ", error: " + err + ").",
        );
      },
    );
  }

  setFormTemplate(data, callback) {
    store.dispatch({
      type: types.SET_FORM_TEMPLATE,
      payload: {
        data,
      },
    });
    if (typeof callback === "function") {
      callback();
    }
  }

  clearFormTemplate(callback) {
    store.dispatch({
      type: types.CLEAR_FORM_TEMPLATE,
      payload: {},
    });
    if (typeof callback === "function") {
      callback();
    }
  }
}

export default new Flagship();



// WEBPACK FOOTER //
// ./src/core/flagship.js